import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingSettingsMap,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { scaledCoordinate } from '../drawing.helper'
import { ChartEvents } from '../../chart.events'
import { primaryDrawingColor } from '../../../../assets/colors'

export type TextToolType = 'normal' | 'anchored'

export type TextToolState = {
    text: string,
    p1?: Point,
    width?: number
}

export type TextToolOptions = {
    textType: TextToolType
}

export type TextToolSettings = {
    text: string,
    textColor: ColorValue,
    fontSize: number,
    bold: boolean,
    italic: boolean,
    background: ColorValue,
    border: ColorValue,
    textWrap: boolean
}

const PADDING = 5
const MIN_WIDTH = 200

class TextTool extends DrawingBase<TextToolOptions, TextToolSettings, TextToolState> {
    scaledCoordinates: Record<string, PointCoordinates> = {
        p1: {
            x: 0,
            y: 0
        }
    }
    textLines: [string, number, number][] = []
    boxWidth = 0
    boxHeight = 0
    horizontalPadding = 0
    verticalPadding = 0

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.horizontalPadding = PADDING * this.horizontalPixelRatio
        this.verticalPadding = PADDING * this.verticalPixelRatio
        this.calculateTextAttributes(ctx)
        const { p1 } = this.scaledCoordinates

        if (!p1.x) return
        const { textColor, background, textWrap } = this._settings
        ctx.beginPath()
        ctx.rect(p1.x, p1.y, this.boxWidth, this.boxHeight)
        ctx.globalAlpha = background.opacity
        ctx.fillStyle = background.color
        ctx.fill()
        this.setLineAttributes(ctx)
        if (textWrap) {
            ctx.globalAlpha = 1
        }
        ctx.stroke()
        ctx.closePath()

        ctx.fillStyle = textColor.color
        ctx.globalAlpha = textColor.opacity
        let startX = p1.x, startY = p1.y
        this.textLines.forEach(([l, w, h]) => {
            ctx.fillText(l, startX + this.horizontalPadding, startY + this.verticalPadding)
            startY += (this.verticalPadding + h)
        })


    };

    calculateTextAttributes(ctx: CanvasRenderingContext2D) {
        const { text: settingsText, textWrap } = this._settings
        const { p1, text = settingsText, width } = this._state

        if (!p1 || !text) return
        const { textType } = this.options

        this.textLines = []
        if (textType === 'anchored') {
            this.scaledCoordinates.p1.x = this.canvasWidth * p1.coordinates!.x
            this.scaledCoordinates.p1.y = this.canvasHeight * p1.coordinates!.y
        } else {
            let p1x = this.timeToCoordinate(p1.x)
            let p1y = this.priceToCoordinate(p1.y)
            if (p1x && p1y) {
                this.scaledCoordinates.p1.x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
                this.scaledCoordinates.p1.y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            }
        }

        ctx.font = this.getFontStyle()

        const textLines = this.getTextLines(ctx)


        this.boxHeight = this.verticalPadding
        this.boxWidth = textWrap && width ? (width * this.horizontalPixelRatio - 2 * this.horizontalPadding) : 0
        ctx.textBaseline = 'top'

        textLines.forEach((v, i) => {
            const dim = ctx.measureText(v)
            if (!textWrap) this.boxWidth = Math.max(this.boxWidth, dim.width)
            const height = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
            this.boxHeight += (height + this.verticalPadding)
            this.textLines.push([v, dim.width, height])
        })

        this.boxWidth += 2 * this.horizontalPadding

    }

    getTextLines(ctx: CanvasRenderingContext2D) {
        const lines: string[] = []
        const { text: settingsText, textWrap } = this._settings
        const { text = settingsText, width } = this._state

        if (!textWrap) {
            return [text]
        } else if (width) {
            const maxWidth = width * this.horizontalPixelRatio - 2 * this.horizontalPadding
            const words = text.split(/\s/g).map(v => v.trim()).filter(v => v)
            let currentLine = ''
            let remainingWidth = maxWidth
            let c = 0
            let word = words[0]

            while (true) {
                if (!word) break
                const dim = ctx.measureText((currentLine.length ? ' ' : '') + word)
                if (dim.width <= remainingWidth) {
                    currentLine += (currentLine.length ? ' ' : '') + word
                    remainingWidth -= dim.width
                    word = words[++c]
                } else {
                    if (currentLine.length) {
                        lines.push(currentLine)
                        currentLine = ''
                        remainingWidth = maxWidth
                    }
                    let line = ''
                    for (let i = 0; i < word.length; i++) {
                        let dim = ctx.measureText(line + word[i])
                        if (dim.width > remainingWidth) {
                            lines.push(line)
                            word = word.substring(i)
                            break
                        }
                        line += word[i]
                    }
                }

                if (remainingWidth === 0 && currentLine.length) {
                    lines.push(currentLine)
                    remainingWidth = maxWidth
                    currentLine = ''
                }
            }

            if (currentLine.length) lines.push(currentLine)
        }

        return lines
    }

    getFontStyle() {
        const { fontSize, bold, italic } = this._settings
        return `${italic ? 'italic ' : ''} ${bold ? 'bold ' : ''} ${fontSize * this.verticalPixelRatio}px sans-serif`
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        lineDash = [8 * window.devicePixelRatio, 3 * window.devicePixelRatio]
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.border.opacity
        ctx.lineWidth = window.devicePixelRatio
        ctx.strokeStyle = this._settings.border.color
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        this._state.p1 = {
            id: 'p1',
            x: time,
            y: price
        }

        const { textType } = this.options

        if (textType === 'anchored') {

            this._state.p1.coordinates = {
                x: coordinates.x * window.devicePixelRatio / this.canvasWidth,
                y: coordinates.y * window.devicePixelRatio / this.canvasHeight
            }

        }
        !preview && this.onDrawingEnd()
        if (!preview) return { ...this._state.p1 }
    }
    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, width } = this._state
        const { textWrap } = this._settings
        if (!p1) return []
        if (this.options.textType === 'anchored') {
            handles.push({
                ...p1,
                coordinates: {
                    x: this.canvasWidth * p1.coordinates!.x / this.horizontalPixelRatio,
                    y: this.canvasHeight * p1.coordinates!.y / this.verticalPixelRatio
                }
            })
        } else {
            p1 && handles.push(p1)
        }

        if (p1 && textWrap && width) {
            let x = this.timeToCoordinate(p1.x)
            let y = this.priceToCoordinate(p1.y)

            if (this.options.textType === 'anchored') {
                x = this.canvasWidth * p1.coordinates!.x / this.horizontalPixelRatio as Coordinate
                y = this.canvasHeight * p1.coordinates!.y / this.verticalPixelRatio as Coordinate
            }

            if (x && y) {
                const rightX = x + width
                const time = this.coordinateToTime(rightX as Coordinate)

                const topY = y
                const bottomY = topY + this.boxHeight / this.verticalPixelRatio
                const midY = (topY + bottomY) / 2
                const price = this.coordinateToPrice(midY as Coordinate)
                if (time && price && midY) {
                    handles.push({
                        id: 'p2',
                        x: time,
                        y: price,
                        coordinates: {
                            x: rightX,
                            y: midY
                        }
                    })
                }
            }
        }
        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)

        const { p1 } = this.scaledCoordinates
        if (!p1) return null

        if (scaledX - p1.x > this.boxWidth || scaledX < p1.x || scaledY < p1.y || scaledY - p1.y > this.boxHeight) return null

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    const p = { ...h }
                    if (this.options.textType === 'anchored') {
                        const x = p.coordinates ? p.coordinates.x : this.timeToCoordinate(p.x)
                        const y = p.coordinates ? p.coordinates.y : this.priceToCoordinate(p.y)
                        if (!x || !y) return
                        p.coordinates = {
                            x: x * this.horizontalPixelRatio / this.canvasWidth,
                            y: y * this.verticalPixelRatio / this.canvasHeight
                        }
                    }
                    this._state.p1 = p
                    break
            }
        })
    }


    updateHandle = (id: string, time: Time, price: number) => {
        const { p1 } = this._state
        if (!p1) return
        switch (id) {
            case 'p1':
                const p: Point = { id, x: time, y: price }
                if (this.options.textType === 'anchored') {
                    const x = this.timeToCoordinate(p.x)
                    const y = this.priceToCoordinate(p.y)
                    if (!x || !y) return
                    p.coordinates = {
                        x: x * this.horizontalPixelRatio / this.canvasWidth,
                        y: y * this.verticalPixelRatio / this.canvasHeight
                    }
                }
                this._state.p1 = p
                break
            case 'p2':
                const rightX = this.timeToCoordinate(time) || 0
                const leftX = (this.options.textType === 'anchored' ? p1.coordinates!.x * this.canvasWidth / this.horizontalPixelRatio : this.timeToCoordinate(p1.x)) || 0

                if (leftX && rightX && rightX - leftX >= MIN_WIDTH) {
                    this._state.width = (rightX - leftX)
                }
        }
    }

    onSettingsUpdate = (settings: Partial<TextToolSettings>) => {
        if (settings.textWrap === true && !this._state.width) {
            this._state.width = MIN_WIDTH * this.horizontalPixelRatio
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        return []
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        return []
    }

    getAvailableSettings = (): DrawingSettingGroup<TextToolSettings, SettingValues>[] => {
        const { textType } = this.options
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'text',
                    label: 'Text',
                    type: SettingType.TEXT_AREA,
                    defaultValue: 'Add text',
                    custom: true,
                    visible: true
                },
                {
                    name: 'textColor',
                    label: 'Text Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'fontSize',
                    label: 'Font Size',
                    type: SettingType.FONT,
                    defaultValue: 14,
                    custom: true,
                    visible: true
                },
                {
                    name: 'bold',
                    label: 'Bold',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                },
                {
                    name: 'italic',
                    label: 'Italic',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                },
                {
                    name: 'background',
                    label: 'Background',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#5b85bf',
                        opacity: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'border',
                    label: 'Border',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#667b8b',
                        opacity: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'textWrap',
                    label: 'Text Wrap',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getMiniSettings = () => {
        return ['textColor', 'fontSize'] as (keyof TextToolSettings)[]
    }

    getDrawingType(): keyof DrawingSettingsMap {
        return this.options.textType === 'anchored' ? 'anchoredText' : 'text'
    }

    onDrawingEnd() {
        super.onDrawingEnd()
        this._chart.publishEvent(ChartEvents.OPEN_DRAWING_SETTINGS, { id: this.id, open: true })
    }
}

export default TextTool